const PlummeAccountingBasisCoverage = (() => {
  const incomplete = (reasonCode, coverage = {}) => ({
    ...coverage,
    status: 'INCOMPLETE',
    reason_codes: [...new Set([...(Array.isArray(coverage?.reason_codes) ? coverage.reason_codes : []), reasonCode])],
  });

  const acceptServerCoverage = ({ coverage, responseBusinessId, activeBusinessId, year, recordedSeq, eventCount, period }) => {
    if (!coverage || coverage.authority !== 'server') return incomplete('COVERAGE_NOT_DOCUMENTED');
    if (!['EMPTY', 'COMPLETE', 'INCOMPLETE'].includes(coverage.status)) return incomplete('COVERAGE_STATUS_UNKNOWN', coverage);
    if (!responseBusinessId || responseBusinessId !== activeBusinessId) return incomplete('COVERAGE_BUSINESS_MISMATCH', coverage);
    const expectedPeriod = period || { from: `${year}-01-01`, to: `${year}-12-31` };
    if (coverage.period?.from !== expectedPeriod.from || coverage.period?.to !== expectedPeriod.to) return incomplete('COVERAGE_PERIOD_MISMATCH', coverage);
    if (!['complete_journal_through_watermark', 'paginated_journal_through_watermark'].includes(coverage.read_scope?.mode)) return incomplete('COVERAGE_SCOPE_INCOMPLETE', coverage);
    if (Number(coverage.basis_recorded_seq) !== Number(recordedSeq || 0)) return incomplete('COVERAGE_STALE', coverage);
    if (Number(coverage.read_scope?.event_count) !== Number(eventCount || 0)) return incomplete('COVERAGE_EVENT_COUNT_MISMATCH', coverage);
    return coverage;
  };

  const fallback = incomplete('COVERAGE_NOT_DOCUMENTED');
  // Speed track S1: the business state is published before the journal has been read. Until
  // the server coverage arrives the state carries this client placeholder, which every
  // consumer treats as blocked (never as an empty journal, never as a source projection) and
  // which the journal recovery guard never records as an incident.
  const LOADING_REASON = 'ACCOUNTING_JOURNAL_LOADING';
  const loadingCoverage = (period) => ({ authority: 'client', status: 'LOADING', period: period || null, reason_codes: [LOADING_REASON] });
  const isLoading = (coverage) => coverage?.status === 'LOADING';
  const evaluate = (coverage, { allowEmpty = true, allowImportedStatistics = false, period } = {}) => {
    if (!coverage) return { classification: 'MISSING', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_NOT_DOCUMENTED' };
    if (isLoading(coverage)) return { classification: 'LOADING', valid: true, readable: false, complete: false, loading: true, reasonCode: LOADING_REASON };
    if (coverage.authority !== 'server') return { classification: 'INVALID', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_AUTHORITY_INVALID' };
    if (!['EMPTY', 'COMPLETE', 'INCOMPLETE'].includes(coverage.status)) return { classification: 'INVALID', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_STATUS_UNKNOWN' };
    if (period && (coverage.period?.from !== period.from || coverage.period?.to !== period.to)) return { classification: 'INVALID', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_PERIOD_MISMATCH' };
    if (!['complete_journal_through_watermark', 'paginated_journal_through_watermark'].includes(coverage.read_scope?.mode)) return { classification: 'INVALID', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_SCOPE_INCOMPLETE' };
    if (!Number.isInteger(Number(coverage.basis_recorded_seq)) || Number(coverage.basis_recorded_seq) < 0) return { classification: 'INVALID', valid: false, readable: false, complete: false, reasonCode: 'COVERAGE_WATERMARK_INVALID' };
    if (coverage.status === 'INCOMPLETE') {
      const importedReadable = allowImportedStatistics
        && (coverage.reason_codes || []).every((reason) => reason === 'IMPORTED_STATISTICS_REPORTING_BASIS')
        && coverage.reporting_basis?.kind === 'imported_accounting_statistics'
        && coverage.reporting_basis?.source === 'accountingImportReportStatsByYear'
        && coverage.reporting_basis?.validated === true
        && Number(coverage.reporting_basis?.year) === Number(String(coverage.period?.from || '').slice(0, 4));
      return { classification: 'INCOMPLETE', valid: true, readable: importedReadable, complete: false, reasonCode: importedReadable ? null : 'ACCOUNTING_BASIS_INCOMPLETE' };
    }
    if (coverage.status === 'EMPTY') return { classification: 'EMPTY', valid: true, readable: allowEmpty, complete: false, reasonCode: allowEmpty ? null : 'ACCOUNTING_BASIS_EMPTY_NOT_ALLOWED' };
    return { classification: 'COMPLETE', valid: true, readable: true, complete: true, reasonCode: null };
  };
  // EMPTY is a server-confirmed readable zero basis. Missing, unknown,
  // incomplete and client-forged statuses fail closed.
  const isBlocked = (coverage, options) => !evaluate(coverage, options).readable;
  // A verified journal may still need an opening balance. Keep its accounting
  // consumers blocked, but let the user continue importing the missing basis.
  const isImportedOpeningPending = (coverage) => coverage?.status === 'INCOMPLETE'
    && evaluate(coverage).valid
    && coverage.reason_codes?.includes('IMPORTED_OPENING_NOT_JOURNALED')
    && coverage.reason_codes.every(code => ['IMPORTED_OPENING_NOT_JOURNALED', 'REPORT_OPENING_BASIS_MISSING'].includes(code));
  const isHydratedStateBlocked = (state, options = {}) => {
    if (!Array.isArray(state?.accountingEvents)) return false;
    const year = Number(state?.activeYear);
    const period = Number.isInteger(year) ? { from: `${year}-01-01`, to: `${year}-12-31` } : undefined;
    return isBlocked(state?.accountingBasisCoverage, { period, ...options });
  };
  const blockedReadModel = (kind, coverage) => ({
    blocked: true,
    loading: isLoading(coverage),
    blockReason: isLoading(coverage) ? 'accounting_journal_loading' : 'accounting_basis_incomplete',
    kind,
    accountingBasis: coverage || fallback,
  });
  return {
    acceptServerCoverage,
    evaluate,
    isBlocked,
    isImportedOpeningPending,
    isLoading,
    loadingCoverage,
    isHydratedStateBlocked,
    blockedReadModel,
    blockedReports: (coverage) => ({ blocked: true, loading: isLoading(coverage), accountingBasis: coverage || fallback, result: null, trial: null, chart: null, dimensions: null }),
    blockedVat: (year, coverage, state, paymentAccounts) => ({ year, blocked: true, loading: isLoading(coverage), reviewRequired: true, accountingBasis: coverage || fallback, terms: [], totals: null, vatRegistered: Boolean(state.settings?.vatRegistered), paymentAccounts }),
  };
})();

Object.assign(globalThis, { PlummeAccountingBasisCoverage });
